/**
 * JWT Utilities for OAuth Emulation
 *
 * Generates ephemeral RSA key pairs and signs JWTs for OpenID Connect id_tokens.
 * The corresponding public key is served via JWKS endpoints so apps that verify
 * JWT signatures via OIDC discovery will succeed.
 *
 * @module
 */

import crypto from 'node:crypto';

// ─────────────────────────────────────────────────────────────────────────────
// Key Management
// ─────────────────────────────────────────────────────────────────────────────

export interface EphemeralKeyPair {
  privateKey: crypto.KeyObject;
  publicKey: crypto.KeyObject;
  kid: string;
}

/** Generate an RSA-2048 key pair for JWT signing. Called once on startup. */
export function generateKeyPair(): EphemeralKeyPair {
  const { privateKey, publicKey } = crypto.generateKeyPairSync('rsa', {
    modulusLength: 2048,
  });
  const kid = crypto.randomUUID();
  return { privateKey, publicKey, kid };
}

// ─────────────────────────────────────────────────────────────────────────────
// JWT Signing
// ─────────────────────────────────────────────────────────────────────────────

export interface IdTokenClaims {
  iss: string;
  aud: string;
  sub: string;
  email: string;
  email_verified?: boolean;
  name: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
  nonce?: string;
  [key: string]: unknown;
}

/** Sign a JWT id_token with the ephemeral key pair. */
export function signIdToken(
  claims: IdTokenClaims,
  keyPair: EphemeralKeyPair
): string {
  const now = Math.floor(Date.now() / 1000);

  const header = {
    alg: 'RS256',
    typ: 'JWT',
    kid: keyPair.kid,
  };

  const payload = {
    ...claims,
    iat: now,
    exp: now + 3600,
  };

  const headerB64 = base64url(JSON.stringify(header));
  const payloadB64 = base64url(JSON.stringify(payload));
  const signingInput = `${headerB64}.${payloadB64}`;

  const signature = crypto.sign('sha256', Buffer.from(signingInput), keyPair.privateKey);
  const signatureB64 = base64url(signature);

  return `${signingInput}.${signatureB64}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// JWKS
// ─────────────────────────────────────────────────────────────────────────────

/** Build a JWKS response containing the public key. */
export function buildJwks(keyPair: EphemeralKeyPair): object {
  const jwk = keyPair.publicKey.export({ format: 'jwk' });
  return {
    keys: [
      {
        ...jwk,
        kid: keyPair.kid,
        alg: 'RS256',
        use: 'sig',
      },
    ],
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function base64url(input: string | Buffer): string {
  const buf = typeof input === 'string' ? Buffer.from(input) : input;
  return buf.toString('base64url');
}
